import * as amplitude from "@amplitude/analytics-browser";
import { teamContext } from "@redotech/feature-flags-model/context";
import { featureFlag } from "@redotech/openfeature-contracts/flag";
import { useHandler } from "@redotech/react-util/hook";
import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { useTeamFeatureFlag } from "@redotech/redo-merchant-app-common/use-team-feature-flag";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import {
  PopupEvent,
  StandalonePopupType,
} from "@redotech/redo-onboarding-common/popup-events";
import { memo, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { isWelcomeRoute } from "../../onboarding/first-install-redirect";
import {
  PopupEventTiming,
  secondsSince,
  trackPopupEvent,
} from "../popup-analytics";
import {
  hasShownOnboardingThisSession,
  markOnboardingShownInSession,
} from "../use-feature-onboarding";
import { isEligibleForCreditIncentive } from "./credit-incentive-eligibility";
import { CreditIncentiveModal } from "./credit-incentive-modal";
import {
  canShowCreditIncentive,
  readCreditIncentiveHistory,
  recordCompletion,
  recordDismissal,
  writeCreditIncentiveHistory,
} from "./credit-incentive-storage";

/** Offer the Checkout+ credit incentive demo modal to eligible store admins. */
const creditIncentiveModalFlag = featureFlag(
  "credit-incentive-modal",
  teamContext,
).boolean(false);

/** `?credit-incentive-preview` forces the modal without touching its history. */
const PREVIEW_PARAM = "credit-incentive-preview";

/** Long enough for the popup manager's own pick to land first, so two modals never stack. */
const YIELD_TO_POPUPS_MS = 2500;

/** Each step's Amplitude name, and the shared popup event it sends Datadog. */
const EVENTS = {
  shown: { amplitude: "credit-incentive-modal-shown", popup: PopupEvent.SHOWN },
  bookClicked: {
    amplitude: "credit-incentive-modal-book-clicked",
    popup: PopupEvent.CTA_CLICKED,
  },
  booked: {
    amplitude: "credit-incentive-modal-booking-confirmed",
    popup: PopupEvent.CTA_CONFIRMED,
  },
  dismissed: {
    amplitude: "credit-incentive-modal-dismissed",
    popup: PopupEvent.DISMISSED,
  },
} as const;

/** The flag lookup needs a team, which the shell may not have loaded yet. */
export const CreditIncentiveHost = memo(function CreditIncentiveHost() {
  const team = useContext(TeamContext);
  return team ? <CreditIncentiveForTeam /> : null;
});

const CreditIncentiveForTeam = memo(function CreditIncentiveForTeam() {
  const team = useContext(TeamContext);
  const flag = useTeamFeatureFlag(creditIncentiveModalFlag);
  const user = useContext(UserContext);
  const location = useLocation();
  const screenType = useScreenType();
  const [open, setOpen] = useState(false);
  /** `performance.now()` when the modal appeared, for time-to-act. */
  const shownAt = useRef<number | null>(null);

  const preview = useMemo(
    () => new URLSearchParams(location.search).has(PREVIEW_PARAM),
    [location.search],
  );
  const teamId = team?._id.toString();
  const userId = user?._id?.toString();

  // The flag gates the preview too, so no store sees this before it is on.
  const eligible =
    flag.enabled &&
    !!team &&
    !!teamId &&
    !!userId &&
    screenType !== ScreenType.MOBILE &&
    !isWelcomeRoute(location.pathname) &&
    (preview ||
      (isEligibleForCreditIncentive(team, user, location.pathname) &&
        canShowCreditIncentive(
          readCreditIncentiveHistory(teamId, userId),
          Temporal.Now.instant(),
        )));

  /** A preview is internal traffic, so it records nothing. */
  const track = useHandler(
    (step: keyof typeof EVENTS, timing: PopupEventTiming = {}) => {
      if (preview) {
        return;
      }
      amplitude.logEvent(EVENTS[step].amplitude, { teamId, ...timing });
      trackPopupEvent(
        EVENTS[step].popup,
        StandalonePopupType.CREDIT_INCENTIVE,
        teamId,
        timing,
      );
    },
  );

  const timeSinceShown = (): PopupEventTiming =>
    shownAt.current === null
      ? {}
      : { secondsSinceShown: secondsSince(shownAt.current) };

  useEffect(() => {
    if (!eligible || open) {
      return;
    }
    if (preview) {
      setOpen(true);
      return;
    }
    const timer = setTimeout(() => {
      if (hasShownOnboardingThisSession()) {
        return;
      }
      markOnboardingShownInSession();
      shownAt.current = performance.now();
      track("shown");
      setOpen(true);
    }, YIELD_TO_POPUPS_MS);
    return () => clearTimeout(timer);
  }, [eligible, open, preview, track]);

  const persist = useHandler(
    (update: typeof recordCompletion | typeof recordDismissal) => {
      if (preview || !teamId || !userId) {
        return;
      }
      const history = readCreditIncentiveHistory(teamId, userId);
      writeCreditIncentiveHistory(
        teamId,
        userId,
        update(history, Temporal.Now.instant()),
      );
    },
  );

  const onDismiss = useHandler((videoSecondsWatched: number) => {
    setOpen(false);
    track("dismissed", { ...timeSinceShown(), videoSecondsWatched });
    persist(recordDismissal);
  });

  /**
   * Opening the calendar retires the offer: Google's booking page never tells
   * us whether a slot was taken, so the click is the strongest signal we get.
   */
  const onBookingStarted = useHandler((videoSecondsWatched: number) => {
    track("bookClicked", { ...timeSinceShown(), videoSecondsWatched });
    persist(recordCompletion);
  });

  const onBooked = useHandler(() => {
    track("booked", timeSinceShown());
  });

  const onFinished = useHandler(() => {
    setOpen(false);
  });

  if (!open) {
    return null;
  }
  return (
    <CreditIncentiveModal
      onBooked={onBooked}
      onBookingStarted={onBookingStarted}
      onDismiss={onDismiss}
      onFinished={onFinished}
    />
  );
});
